import { ApolloCache, RefetchQueriesOptions, useApolloClient } from '@apollo/client';

// Exported so the allow-list can be asserted at runtime as well as in types (CRPD-7274).
export const fieldNames = [
  'profile',
  'workspace',
  'workspaceMembers',
  'workspaceVariables',
  'wizard',
  'organisation',
  'organisationMembers',
  'flatOrganisationsHome',
  'flatOrganisationsUser',
  'flatOrganisationsNav',
  'user',
  'assessments',
  'assessmentsScenarioNames',
  'assessmentsList',
  'assessment',
  'assessmentVariables',
  'assessmentRiskRegister',
  'incidents',
  'incidentsCheck',
  'incident',
  'incidentLinkedScenarios',
  'controls',
  'controlsRmpCheck',
  'controlsRmp',
  'assessmentScenariosWizard',
  'control',
  'controlLinkedScenarios',
  'scenario',
  'scenarioRmp',
  'scenarioControls',
  'events',
  'event',
  'controlFrameworkDetails',
  'complianceFramework',
  'filtersFor',
  'controlRmp',
  'controlModifier',
  'scenarioModifier',
  'scenarioIncidents',
  // AI Risk Assistant (ticket 7127) — used by the future GraphQL service to refetch the
  // session after a mutation; the current mock path does not touch the Apollo cache.
  'aiAssistantSession',
  // CRPD-7274 — the Control Maturity page. `frameworkMaturity` covers BOTH depths, because the
  // document aliases them into one operation; refetching by name therefore refreshes the spider
  // legs and the sub-tier rows together and cannot leave one stale.
  'frameworkMaturity',
  'frameworkHierarchyCommentaries',
  // CRPD-7267 — the framework's described tiers. Refetched after a hierarchy CSV import so the
  // content editor's Hierarchy tab and its details panel show the tier names just uploaded.
  'frameworkHierarchy',
] as const;

export type KeyType = (typeof fieldNames)[number];

// Every surface a variable value feeds: the variables lists + the BE-recalculated
// control costs (CRPD-7222 recalcs on upsert AND delete). Variable-save paths on
// cost surfaces refetch with this ONE include array — never chain refetchQuery
// calls (see useCache.test.tsx, which pins the contents).
export const VARIABLE_SAVE_COST_INCLUDES: KeyType[] = [
  'workspaceVariables',
  'assessmentVariables',
  'controls',
  'control',
];

type RefetchQuery = (params?: {
  include?: KeyType[] | 'all';
  options?: RefetchQueriesOptions<ApolloCache<object>, unknown>;
}) => Promise<void>;

export const useCache = () => {
  const client = useApolloClient();

  const refetchQuery: RefetchQuery = async (params) => {
    const { include, options } = params || {};

    await client.refetchQueries({
      include,
      optimistic: false,
      ...options,
    });
  };

  return { refetchQuery };
};
